import React, { useState, useEffect } from "react";
import { FaTrashAlt } from "react-icons/fa";
import BottomNavbar from "../../components/BottomNavbar";
import {
  getRequest,
  deleteRequest,
  postWithFormData,
  putWithFormData,
} from "../../helpers/api.helper";
import { toast } from "react-toastify";
import Gnavbar from "../../components/Gnavbar";
import { UPLOAD_URL } from "../../utils/constants";

type StoryType = {
  id: number;
  image_path: string;
  title: string | null;
  marriage_date: string | null;
  description: string;
  created_at: string;
  approval_status?: "inactive" | "approved";
};

const formatDisplayDate = (value: string | null | undefined) => {
  if (!value) return "";
  const [year, month, day] = value.split("T")[0].split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const SuccessStories = () => {
  const [stories, setStories] = useState<StoryType[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [marriageDate, setMarriageDate] = useState("");
  const [openCalendar, setOpenCalendar] = useState(false);
  const [calendarDraftDate, setCalendarDraftDate] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const res = await getRequest(`/bureau/stories`);
      if (res.success) setStories(res.data || []);
      else toast.error(res.message);
    } catch (err) {
      console.error(err);
      toast.error("Error fetching success stories");
    }
  };

  const handleEdit = (story: StoryType) => {
    setEditId(story.id);
    setTitle(story.title || "");
    setDescription(story.description || "");
    setMarriageDate(
      story.marriage_date ? story.marriage_date.split("T")[0] : ""
    );
    setPreview(UPLOAD_URL + "/bureaus/stories/" + story.image_path);
    setImage(null);

    setShowEditModal(true);
  };

  const resetForm = () => {
    setImage(null);
    setPreview(null);
    setTitle("");
    setMarriageDate("");
    setDescription("");
    setCalendarDraftDate("");
    setOpenCalendar(false);
  };

  const openMarriageDatePicker = () => {
    setCalendarDraftDate(marriageDate);
    setOpenCalendar(true);
  };

  const applyMarriageDate = () => {
    setMarriageDate(calendarDraftDate);
    setOpenCalendar(false);
  };

  const handleUpdate = async (e: any) => {
    e.preventDefault();

    if (!editId) return;

    const formData = new FormData();

    if (image) {
      formData.append("image", image);
    }

    formData.append("title", title.trim());
    formData.append("description", description.trim());
    formData.append("marriage_date", marriageDate);

    try {
      setUploading(true);

      const res = await putWithFormData(`/bureau/stories/${editId}`, formData);

      if (res.success) {
        toast.success("Story updated!");
        fetchStories();
        setShowEditModal(false);
        setEditId(null);
        setImage(null);
        setPreview(null);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Error updating story");
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      //@ts-ignore
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = async (e: any) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!image) return toast.warn("Select an image first");
    if (!trimmedTitle) return toast.warn("Enter bride groom name");
    if (!trimmedDescription) return toast.warn("Enter description");
    if (!marriageDate) return toast.warn("Select marriage date");

    const formData = new FormData();
    formData.append("image", image);
    formData.append("title", trimmedTitle);
    formData.append("description", trimmedDescription);
    formData.append("marriage_date", marriageDate);

    try {
      setUploading(true);

      const res = await postWithFormData("/bureau/stories", formData);

      if (res.success) {
        toast.success("Success story added!");

        resetForm();
        fetchStories();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error uploading story");
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const res = await deleteRequest(`/bureau/stories/${deleteId}`, {});
      if (res.success) {
        toast.success("Story deleted!");
        setStories((prev) => prev.filter((s) => s.id !== deleteId));
      } else toast.error(res.message);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting story");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };
  useEffect(() => {
    if (showEditModal || showDeleteModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showEditModal, showDeleteModal]);

  return (
    <div className="flex flex-col h-screen">
      <Gnavbar />

      <div className="bg-gray-50 py-5 px-4 min-h-[80vh]">
        <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-semibold text-center text-blue-600 mb-8">
            Bureau Success Stories
          </h2>

          {/* Upload Form */}
          <form onSubmit={handleUpload} className="space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
            {preview && (
              <div className="text-center">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-32 h-32 object-cover mx-auto rounded-md mt-3"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter bride groom name
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter bride groom name"
                required
                className="w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Write a short success story description..."
              required
              className="w-full border border-gray-300 rounded-md p-2"
              rows={3}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marriage Date
              </label>

              <div className="relative">
                <input
                  type="text"
                  value={formatDisplayDate(marriageDate)}
                  onChange={() => {}} // disable manual typing
                  placeholder="Select marriage date (DD/MM/YYYY)"
                  required
                  className="w-full border px-3 py-2 rounded-md pr-10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                  readOnly
                  onClick={openMarriageDatePicker}
                />

                {/* Calendar Icon */}
                <span
                  className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-600 hover:text-blue-600"
                  onClick={openMarriageDatePicker}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10m-11 7h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z"
                    />
                  </svg>
                </span>
              </div>
            </div>

            {/* Calendar Modal */}
            {openCalendar && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-80">
                  <h3 className="text-lg font-semibold mb-4">
                    Select Marriage Date
                  </h3>

                  <input
                    type="date"
                    value={calendarDraftDate}
                    className="w-full border px-3 py-2 rounded-md mb-4"
                    onChange={(e) => setCalendarDraftDate(e.target.value)}
                  />

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setOpenCalendar(false)}
                      className="w-full mt-2 bg-gray-200 py-2 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={applyMarriageDate}
                      className="w-full mt-2 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
            >
              {uploading ? "Saving..." : "Add Story"}
            </button>
          </form>

          {/* Stories List */}
          <div className="mt-8 mb-12">
            <h3 className="text-xl font-semibold mb-3 text-gray-700">
              Uploaded Stories
            </h3>
            {stories.length === 0 ? (
              <p className="text-gray-500">No success stories added yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {stories.map((story) => (
                  <div key={story.id} className="relative">
                    <img
                      src={UPLOAD_URL + "/bureaus/stories/" + story.image_path}
                      alt="Story"
                      className="w-full h-40 object-cover rounded-md shadow-sm"
                    />
                    <div className="mt-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          story.approval_status === "approved"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {story.approval_status === "approved"
                          ? "Approved"
                          : "Inactive"}
                      </span>
                      <p className="text-sm font-semibold text-gray-800">
                        {story.title || "No Title"}
                      </p>

                      <p className="text-xs text-gray-600 mt-1">
                        {story.description || "No description"}
                      </p>

                      {story.marriage_date && (
                        <p className="text-xs text-blue-600 mt-1">
                          Marriage Date:{" "}
                          {new Date(story.marriage_date).toLocaleDateString(
                            "en-GB"
                          )}
                        </p>
                      )}

                      <p className="text-xs text-gray-400 mt-1">
                        Posted On:{" "}
                        {new Date(story.created_at).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <div className="absolute top-1 right-1 flex gap-2">
                      <button
                        onClick={() => handleEdit(story)}
                        className="bg-blue-600 text-white p-1 rounded-full hover:bg-blue-700 transition"
                      >
                        ✏️
                      </button>

                      <button
                        onClick={() => confirmDelete(story.id)}
                        className="bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition"
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80 text-center">
            <h3 className="text-lg font-semibold mb-4">
              Delete this success story?
            </h3>
            <div className="flex justify-center gap-3">
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center p-2 z-50"
          onClick={() => {
            setShowEditModal(false);
            setEditId(null);
            setImage(null);
            setPreview(null);
            setTitle("");
            setDescription("");
            setMarriageDate("");
          }}
        >
          <div
            className="bg-white w-96 p-6 rounded-lg shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowEditModal(false);
                setEditId(null);
                setImage(null);
                setPreview(null);
                setTitle("");
                setDescription("");
                setMarriageDate("");
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-black text-lg"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-4">Edit Success Story</h3>

            <form onSubmit={handleUpdate} className="space-y-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full border rounded px-3 py-2"
              />

              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="w-24 h-24 object-cover rounded-md"
                />
              )}

              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Bride & Groom Name"
                className="w-full border px-3 py-2 rounded"
                required
              />

              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description"
                className="w-full border px-3 py-2 rounded"
                required
              />

              <input
                type="date"
                value={marriageDate}
                onChange={(e) => setMarriageDate(e.target.value)}
                className="w-full border px-3 py-2 rounded"
                required
              />

              <button
                type="submit"
                disabled={uploading}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
              >
                {uploading ? "Updating..." : "Update Story"}
              </button>
            </form>
          </div>
        </div>
      )}
      <BottomNavbar />
    </div>
  );
};

export default SuccessStories;
